import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrayHelpers, FieldArray } from 'formik';
import { ButtonFormikTab, CustomButton, ReactSelectForm } from '../../../../../components';
import uniqId from 'uniqid';
import { UniqueIdentifier } from '@dnd-kit/core';

import { AddIcon } from '../../../../../assets/icons/AddIcon';
import { ClassValues } from '../ClassesTag/ClassesTag';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { classSeedingData, classTypesData } from '../../../../../constant/class';
import { ModalAction } from '../../../../../types/modal';
import { openModal } from '../../../../../features/modal/modalSlice';
import './index.scss';
import { ClassFormatEnum, ClassTypeEnum, TeamTournamentType } from '../../../../../types/enums';
import { seedTeamsByDuprRating, shuffleTeams } from './TeamSection.helpers';
import { ShuffleIcon } from '../../../../../assets/icons/ShuffleIcon';
import { RootState } from '../../../../../app/store';
import { useGetCurrentOrganization } from '../../../../../features/organizations/organizationsSelectors';
import { useParams } from 'react-router-dom';
import { InfoCircleFill, SortNumericDownAlt } from 'react-bootstrap-icons';
import { SortableWithCombine } from './SortableWithCombine';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { combineTeams } from '../../../../../apis/class';
import { fetchTicketsForClass } from '../../../../../apis/ticket';
import { Alert, FormLabel, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { TicketStatusEnum } from '../../../../../apis/tournament';
import { TicketRoleEnum } from '../../../../../types/Ticket';
import { traslateItemWithLabel } from '../../../../../utils/traslation.helpers';
import { isTemporaryClassId } from '../../../../../utils/common.helpers';
import { getDisplayName } from '../../../../../utils/userDisplayName';

interface TeamSectionProps {
  setField: (field: string, value: any, shouldValidate?: boolean) => void;
  values: ClassValues & { id?: number };
}

const devTools = process.env.REACT_APP_ENV === 'dev' || false;

export const TeamSection: FC<TeamSectionProps> = (props) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { setField, values } = props;
  const teams = values.teams;
  const currentType = useAppSelector((state: RootState) => state.class.currentType);
  const { communityId } = useParams<{ communityId: string }>();

  const currentOrganization = useGetCurrentOrganization();

  const type = classTypesData.find((el) => el.value === currentType);
  const dispatch = useAppDispatch();
  const isTeamLeague = type?.value === ClassTypeEnum.TEAMS;
  // We lock the number of teams to 24. But BE can support more, so this value can be increased without any issues.
  const isTeamLeagueMax = isTeamLeague && teams.length >= 24;
  const isNewClass = typeof values.classId === 'string';

  const prefix = 'creativeDetail-';
  const modalPrefix = 'modal-';

  const { data } = useQuery({
    queryKey: ['tickets', values.classId],
    queryFn: () => fetchTicketsForClass(values.classId?.toString() || ''),
    enabled: !!values.classId && !isTemporaryClassId(values.classId),
  });

  const showShuffleButton = teams.find(({ seed, shuffled }) => !seed || shuffled);

  const handleOpenTeamModal = useCallback(
    (push: (obj: any) => void) => {
      dispatch(
        openModal({
          currentModal: ModalAction.ADD_TEAM,
          modalOpen: true,
          data: {
            selected: teams,
            title: t(modalPrefix + 'addTeam'),
            push,
            communityId,
          },
        })
      );
    },
    [dispatch, t, teams, communityId]
  );

  const canCombine = (sourceId: UniqueIdentifier, targetId: UniqueIdentifier) => {
    if (sourceId === targetId) {
      return false;
    }
    const sourceTeam = teams.find((t) => t.id === sourceId);
    const targetTeam = teams.find((t) => t.id === targetId);
    if (typeof sourceId !== 'number' || typeof targetId !== 'number') {
      // Temporary teams will have a string id (until its created in BE)
      return false;
    }
    if (!sourceTeam || !targetTeam) {
      return false;
    }
    if (type?.value !== ClassTypeEnum.DOUBLES) {
      return false;
    }
    // We need to verify that both teams only have a single player
    if (sourceTeam.users.length !== 1 || targetTeam.users.length !== 1) {
      return false;
    }

    return true;
  };

  const combineTeamsMutation = useMutation({
    mutationFn: ({
      sourceTeamId,
      targetTeamId,
    }: {
      sourceTeamId: number;
      targetTeamId: number;
    }) => {
      return combineTeams(sourceTeamId, targetTeamId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets', values.classId] });
    },
  });

  // Exclude free roster-member tickets from "orphaned ticket" warnings for TEAMS
  const ticketsWithoutTeams = data?.filter(
    (t) =>
      t.status !== TicketStatusEnum.TRANSFERRED &&
      t.ticketRole !== TicketRoleEnum.ROSTER_MEMBER &&
      !teams.some((team) => team.id === t.teamId)
  );

  // Child classes (Qualified / Consolation) are not a registration surface.
  const displayTicketWarning = !values.parentClassId && !!ticketsWithoutTeams?.length;

  return (
    <div className="container-fluid px-0">
      {isNewClass ? (
        <Alert variant="info" className="mt-3">
          <h5 className="mb-2">{t(prefix + 'saveClassTeams')}</h5>
          <p className="mb-0">{t(prefix + 'saveClassDescription')}</p>
        </Alert>
      ) : (
        <FieldArray name="teams">
          {({ push, remove }: ArrayHelpers) => (
            <>
              <div className="my-3 d-flex justify-content-between align-items-center">
                <h4 className="font-weight-bold">
                  {t(prefix + 'teams')} ({teams.length})
                </h4>
                <div className="d-flex">
                  {!(
                    values.format === ClassFormatEnum.BOX_LEAGUE ||
                    values.format === ClassFormatEnum.BOX_LEAGUE_ELIM
                  ) && (
                    <div>
                      <ReactSelectForm
                        options={classSeedingData.map((item) => traslateItemWithLabel(t, item))}
                        name="seedingType"
                        preselect={true}
                        disabled={!!values.parentClassId}
                        updateFormValue={true}
                        className="d-flex justify-content-center align-items-center mx-2"
                        isSearchable={false}
                      />
                    </div>
                  )}
                  {showShuffleButton && (
                    <>
                      <CustomButton
                        variant="dark"
                        btnText={t(prefix + 'shuffleRank')}
                        cb={() => {
                          const teamsToUpdate = shuffleTeams(teams);
                          const newTeams = teamsToUpdate.sort((a, b) => a.seed - b.seed);
                          setField('teams', [
                            ...newTeams.filter((i) => Boolean(i.seed)),
                            ...newTeams.filter((i) => !Boolean(i.seed)),
                          ]);
                        }}
                        className="d-flex justify-content-center align-items-center me-1"
                      >
                        <ShuffleIcon h="14" w="14" />
                      </CustomButton>
                      <CustomButton
                        variant="dark"
                        btnText={t(prefix + 'seedByDuprRating')}
                        cb={() => {
                          const teamsToUpdate = seedTeamsByDuprRating(
                            teams,
                            type?.value ?? ClassTypeEnum.DOUBLES,
                            values.format
                          );
                          const newTeams = teamsToUpdate.sort((a, b) => a.seed - b.seed);
                          setField('teams', [
                            ...newTeams.filter((i) => Boolean(i.seed)),
                            ...newTeams.filter((i) => !Boolean(i.seed)),
                          ]);
                        }}
                        className="d-flex justify-content-center align-items-center me-1"
                      >
                        <SortNumericDownAlt />
                      </CustomButton>
                    </>
                  )}

                  <CustomButton
                    variant="outline-dark"
                    btnText={t(prefix + 'addTeam')}
                    disabled={isTeamLeagueMax}
                    cb={() => {
                      push({
                        id: uniqId(),
                        name: `Team ${teams.length + 1}`,
                        users: [],
                        captain: null,
                        logo: null,
                        seed: 0,
                        organization: currentOrganization,
                        type: isTeamLeague ? TeamTournamentType.Team : TeamTournamentType.Regular,
                      });
                    }}
                    className="d-flex justify-content-between align-items-center me-1"
                  >
                    <AddIcon />
                  </CustomButton>
                  {isTeamLeague && (
                    <CustomButton
                      variant="outline-dark"
                      btnText={t(prefix + 'addExistingTeam')}
                      disabled={isTeamLeagueMax}
                      cb={() => handleOpenTeamModal(push)}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <AddIcon />
                    </CustomButton>
                  )}
                  {devTools && (
                    <>
                      <ReactSelectForm
                        options={[
                          { label: 'No', value: 0 },
                          { label: 'Yes', value: 1 },
                        ]}
                        name="devAutoFillTeamUsers"
                        preselect={true}
                        disabled={!!values.parentClassId}
                        updateFormValue={true}
                        className="d-flex justify-content-center align-items-center mx-2"
                        isSearchable={false}
                      />
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            DEV ONLY - Teams users will be filled according to the current class
                            structure (singles, doubles, teams) if yes. Otherwise teams will not be
                            automatically randomly filled.{' '}
                          </Tooltip>
                        }
                      >
                        <InfoCircleFill className="ml-2" />
                      </OverlayTrigger>
                    </>
                  )}
                </div>
              </div>
              {displayTicketWarning && (
                <Alert variant="warning">
                  <Alert.Heading>{t(prefix + 'ticketsWarningTitle')}</Alert.Heading>
                  {ticketsWithoutTeams?.length > 0 && (
                    <>
                      <h6 className="mb-0">{t(prefix + 'ticketsWarningExtraTickets')}</h6>
                      <ul className="mb-0">
                        {ticketsWithoutTeams.map((ticket) => (
                          <li key={ticket.id}>
                            {`${ticket.id} - ${getDisplayName(ticket.user)}`}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </Alert>
              )}

              <SortableWithCombine
                items={teams}
                canCombine={canCombine}
                push={push}
                remove={remove}
                classId={values.classId}
                setField={setField}
                combineTeams={async (sourceTeamId: number, targetTeamId: number) =>
                  combineTeamsMutation.mutateAsync({ sourceTeamId, targetTeamId })
                }
                disableCombine={type?.value !== ClassTypeEnum.DOUBLES}
              />
            </>
          )}
        </FieldArray>
      )}
    </div>
  );
};
